import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import { structures, extraKeys } from '../mock/data';
import { useLang } from '../i18n/LanguageContext';
import { Reveal } from '../hooks/useReveal';

export default function Structures() {
  const { t } = useLang();
  return (
    <main className="pt-32 lg:pt-40 pb-24 bg-cream">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Reveal variant="up" className="mb-16 lg:mb-24">
          <div data-stagger="0"><SectionLabel>{t('common.collection')}</SectionLabel></div>
          <h1 data-stagger="1" className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.95] text-dark">
            {t('nav.structures')}
          </h1>
        </Reveal>

        <div className="space-y-24 lg:space-y-32">
          {structures.map((s, i) => {
            const dim = s.dimensionsKey ? t(`tents.dimensions.${s.dimensionsKey}`) : s.dimensions;
            const imgFirst = i % 2 === 0;
            return (
              <div
                key={s.id}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  imgFirst ? '' : 'lg:[&>*:first-child]:order-2'
                }`}
              >
                <Reveal
                  variant={imgFirst ? 'left' : 'right'}
                  className="cinematic img-hover-zoom lg:col-span-7 aspect-[4/3] relative"
                >
                  <img src={s.image} alt={t(`tents.${s.id}.name`)} className="w-full h-full object-cover" />
                  {s.comingSoon && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                      <div className="bg-cream/90 backdrop-blur-sm border border-warm px-8 py-4 md:px-12 md:py-6 shadow-xl -rotate-[18deg] w-[120%] text-center">
                        <span className="text-3xl md:text-5xl lg:text-6xl tracking-luxury uppercase text-dark font-semibold whitespace-nowrap">
                          {t('common.comingSoon')}
                        </span>
                      </div>
                    </div>
                  )}
                </Reveal>
                <Reveal variant={imgFirst ? 'right' : 'left'} className="lg:col-span-5">
                  <div data-stagger="0" className="flex items-center gap-3 mb-3">
                    <p className="text-[10px] tracking-luxury uppercase text-gold">{t(`tents.${s.id}.tagline`)}</p>
                    {s.comingSoon && (
                      <span className="text-[10px] tracking-luxury uppercase text-muted-warm border border-warm px-2 py-0.5">
                        {t('common.comingSoonLong')}
                      </span>
                    )}
                  </div>
                  <h2 data-stagger="1" className="font-serif-display text-4xl md:text-5xl text-dark mb-6 leading-tight">
                    {t(`tents.${s.id}.name`)}
                  </h2>
                  <p data-stagger="2" className="text-[15px] text-muted-warm leading-relaxed mb-8 font-light">
                    {t(`tents.${s.id}.description`)}
                  </p>
                  <dl data-stagger="3" className="grid grid-cols-2 gap-x-8 gap-y-4 mb-10">
                    {[
                      [t('specs.capacity'), s.capacity],
                      [t('specs.dimensions'), dim],
                      [t('specs.material'), s.material],
                      [t('specs.setup'), t(`tents.${s.id}.setup`)],
                    ].map(([k, v]) => (
                      <div key={k} className="border-b border-warm pb-3">
                        <dt className="text-[10px] tracking-luxury uppercase text-muted-warm mb-1">{k}</dt>
                        <dd className="text-sm text-dark">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <div data-stagger="4">
                    {s.comingSoon ? (
                      <Link to="/contact" className="inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-dark border border-warm px-7 py-3.5 hover:border-dark transition-colors duration-300 group">
                        {t('cta.keepMeInformed')}
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </Link>
                    ) : (
                      <Link to="/contact" className="inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase text-dark border border-dark px-7 py-3.5 hover:bg-dark hover:text-cream transition-colors duration-300 group">
                        {t('cta.requestStructure')}
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                      </Link>
                    )}
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>

        <Reveal variant="up" className="mt-32 pt-20 border-t border-warm">
          <h2 data-stagger="0" className="font-serif-display text-4xl md:text-5xl text-dark mb-14">{t('extras.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {extraKeys.map((k, i) => (
              <div key={k} data-stagger={i + 1} className="p-8 lg:p-10 bg-cream-soft border border-warm">
                <span className="gold-divider block mb-6" />
                <h3 className="font-serif-display text-2xl text-dark mb-4">{t(`extras.${k}.title`)}</h3>
                <p className="text-sm text-muted-warm leading-relaxed font-light">{t(`extras.${k}.desc`)}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
